import type { AdminTemplatesParams, AdminTemplatesQueryInput } from '../-types';

/**
 * The columns the route sorts by — `AdminQuestionnaireTemplatesListSortColumns`
 * in the modules contract. The query pattern is anchored, so a value outside
 * the set fails validation and 400s the list; an unlisted sort is dropped here.
 */
const SORTABLE = new Set([
  'name',
  'createdAt',
  'updatedAt',
  'questionCount',
  'timesUsedAsTemplate',
  'isActive',
]);

/**
 * The filter keys the list's `buildFilterClause` maps: `name` is a LIKE match,
 * `isActive` an exact match against the int column. Anything else is dropped
 * server-side, so it is dropped here too rather than sent.
 */
const FILTERABLE = new Set(['name', 'isActive']);

/** `count` is capped at the contract's `MAX_PAGE_SIZE`. */
export const MAX_COUNT = 500;

/** Keeps a `field:DIR` pair only when the field is one the route accepts. */
function toSort(sort: string | undefined): string | undefined {
  if (!sort) return undefined;
  const [field, dir] = sort.split(':');
  if (!field || !SORTABLE.has(field)) return undefined;
  return dir === 'DESC' ? `${field}:DESC` : `${field}:ASC`;
}

/**
 * Serialises the table's filter map into the `filter` JSON blob.
 *
 * `useTable` keeps multi-values, but `name` is a LIKE match and `isActive` a
 * single flag, so only the first value of each is meaningful. The active filter
 * is sent as the `'1'` / `'0'` the int column compares against.
 */
export function buildAdminTemplatesFilterJson(
  filters: Record<string, string[]> | undefined,
): string | undefined {
  if (!filters) return undefined;
  const out: Record<string, string> = {};
  for (const [key, values] of Object.entries(filters)) {
    if (!FILTERABLE.has(key)) continue;
    const first = values?.[0]?.trim();
    if (!first) continue;
    out[key] = key === 'isActive' ? (first === '1' ? '1' : '0') : first;
  }
  return Object.keys(out).length > 0 ? JSON.stringify(out) : undefined;
}

/**
 * Turns table state into the endpoint's parameters — the single conversion
 * point, so the route loader and the page's hook land on the same cache entry.
 *
 * **`page` is decremented.** The route takes a 0-based page (`z.coerce.number().min(0)`)
 * while `useTable` counts from 1.
 */
export function toAdminTemplatesParams(
  input: AdminTemplatesQueryInput,
): AdminTemplatesParams {
  const sort = toSort(input.sort);
  const filter = buildAdminTemplatesFilterJson(input.filters);

  return {
    page: Math.max(0, input.page - 1),
    count: Math.min(Math.max(1, input.limit), MAX_COUNT),
    ...(sort ? { sort } : {}),
    ...(filter ? { filter } : {}),
  };
}

export function buildAdminTemplatesQs(params: AdminTemplatesParams): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  return qs.toString();
}
